import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User, Icon, Sparkles } from 'lucide-react';
import { horseHead } from '@lucide/lab';
import { supabase } from '@/integrations/supabase/client';

interface UserInterest {
  id: string;
  swiss_equestrian_rider_id: string;
  horse_name?: string;
  user_id: string;
}

interface InterestWithName extends UserInterest {
  rider_name?: string;
}

export const UserInterestsQuickSearch: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [interests, setInterests] = useState<InterestWithName[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchUserInterests();
  }, []);

  const fetchUserInterests = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: userInterests, error } = await supabase
        .from('user_rider_interests')
        .select('*')
        .eq('user_id', user.id)
        .limit(6);

      if (error) {
        console.error('Error fetching user interests:', error);
        return;
      }

      const interestsWithNames = await Promise.all(
        (userInterests || []).map(async (interest) => {
          try {
            const { data: participants } = await supabase
              .from('participants')
              .select('rider_name')
              .eq('swiss_equestrian_rider_id', interest.swiss_equestrian_rider_id)
              .limit(1);

            return {
              ...interest,
              rider_name: participants?.[0]?.rider_name
            };
          } catch (error) {
            console.error('Error fetching rider name:', error);
            return interest;
          }
        })
      );

      setInterests(interestsWithNames);
    } catch (error) {
      console.error('Error in fetchUserInterests:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInterestClick = (interest: InterestWithName) => {
    if (interest.horse_name) {
      const encodedQuery = encodeURIComponent(interest.horse_name.replace(/\s+/g, '+'));
      navigate(`/search/q/${encodedQuery}`);
    } else {
      navigate(`/search/rider/${interest.swiss_equestrian_rider_id}`);
    }
  };

  if (isLoading) return null;
  if (interests.length === 0) return null;

  return (
    <div className="mt-4 animate-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center gap-2 mb-2.5">
        <Sparkles className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="text-xs uppercase tracking-widest text-muted-foreground font-sans font-medium">
          {t('search.quickAccess')}
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        {interests.map((interest, index) => (
          <button
            key={interest.id}
            onClick={() => handleInterestClick(interest)}
            className="group flex items-center gap-2 px-3 py-1.5 rounded-full bg-surface-container-high text-on-surface text-xs font-medium hover:bg-surface-container-highest transition-colors duration-200 animate-in slide-in-from-left-2"
            style={{ animationDelay: `${index * 80}ms` }}
          >
            <div className="flex items-center justify-center w-4 h-4 rounded-full bg-surface-container">
              {interest.horse_name ? (
                <Icon iconNode={horseHead} size={10} className="text-muted-foreground" />
              ) : (
                <User className="w-2.5 h-2.5 text-muted-foreground" />
              )}
            </div>
            <div className="flex flex-col items-start min-w-0">
              <span className="truncate max-w-32">
                {interest.horse_name || interest.rider_name || 'Unknown'}
              </span>
              {interest.horse_name && interest.rider_name && (
                <span className="text-[10px] text-muted-foreground truncate max-w-28 leading-none">
                  {interest.rider_name}
                </span>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};
